'use no memo';

import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
const DEFAULT_DEBOUNCE_MS = 300;

/**
 * 値の変更をデバウンスして返す。API プレビュー等の連続トリガー抑制に使用する。
 *
 * デバウンス機構は {@link useDebouncedCallback} と同じ lodash-es `debounce` に統一している
 * （コールバック版が必要な場合は `useDebouncedCallback` を使うこと）。`debounce` は cancel を
 * 持つ命令的な可変オブジェクトのため、両フックとも `'use no memo'` で React Compiler のメモ化から外す。
 *
 * NOTE: `value` は参照比較される。オブジェクト/配列を渡す場合は、内容が同じなら参照も
 * 安定するよう呼び出し側で `useMemo` すること。毎レンダー新しい参照を渡すと debounce が
 * リセットされ続け、いつまでも確定しない。
 */
export function useDebouncedValue<T>(value: T, delayMs: number = DEFAULT_DEBOUNCE_MS): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const setDebounced = useMemo(() => debounce((next: T) => setDebouncedValue(next), delayMs), [delayMs]);
  useEffect(() => {
    setDebounced(value);
    return () => setDebounced.cancel();
  }, [value, setDebounced]);
  return debouncedValue;
}